import React from 'react';

import { useInView } from 'react-intersection-observer';

import { Text } from '@change/ds-core-ui/Text';

import { Translate } from '@change-corgi/core-react/i18n';

import { LazyComponent } from '#src/app/shared/components/layout/lazy';
import { capitalizeTopic } from '#src/app/shared/utils/topic';

import { TextMessage } from './components/TextMessage';
import type { VideoMessageProp } from './components/VideoMessage';
import { useSupporterVoices } from './hooks/useSupporterVoices';

/**
 * VideoMessage is loaded in its own chunk so the petitionVideo barrel — and the
 * @mux/mux-player-react it pulls in (the player here will move to the Mux-backed
 * PetitionVideoPlayer) — stays out of the topic page's initial bundle and is fetched on
 * demand when a card scrolls into view. The dynamic import must wrap the whole VideoMessage,
 * not just the player: the barrel is not tree-shaken (no `sideEffects` flag), so any static
 * import from it would drag mux back into the initial graph.
 */
const loadVideoMessage = async (): Promise<React.ComponentType<VideoMessageProp>> =>
	(await import(/* webpackChunkName: "pages/topic-details-supporterVoicesVideoMessage" */ './components/VideoMessage'))
		.VideoMessage;

const LazyVideoMessage: React.FC<VideoMessageProp> = (props) => {
	const { ref, inView } = useInView({
		triggerOnce: true,
		rootMargin: '0px 200px',
	});

	// Reserve the same box PetitionVideoPlayer defaults to (DEFAULT_ASPECT_RATIO = 3/4 at the 326px card
	// height ≈ 245px wide), so swapping the placeholder for the loaded video doesn't shift the row.
	const placeholder = <div className="bg-background-raised aspect-3/4 h-[326px] max-w-[75vw] rounded-lg" aria-hidden />;

	if (!inView) {
		return <div ref={ref}>{placeholder}</div>;
	}

	return (
		<LazyComponent
			loadComponent={loadVideoMessage}
			fallback={placeholder}
			/* oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions */
			{...(props as any)}
		/>
	);
};

export const SupporterVoices: React.FC<EmptyProps> = () => {
	const {
		data: { shouldShowSupporterVoices, supporterVoices, topicName, totalSupporterVoices },
	} = useSupporterVoices();

	// If there are less than 3 supporter voices, we don't show the section
	if (!shouldShowSupporterVoices) return null;

	return (
		<div className="flex flex-col gap-10">
			<Text as="h2" variant="heading" className="text-typography-strong" data-qa="supporter-voices-heading">
				<Translate
					value="corgi.topic_page.supporter_voices.title.headline"
					replacements={{ count: totalSupporterVoices, topic: capitalizeTopic(topicName) }}
				/>
			</Text>

			<div className="b-1 scroll-ps-[10vw] scroll-pe-[10vw] overflow-x-auto md:pb-3">
				<div className="flex gap-6 pl-1">
					{supporterVoices.map((voice) => {
						if (voice.__typename === 'PetitionSupporterComment') {
							return <TextMessage supporterVoice={voice} key={voice.id} />;
						}

						return <LazyVideoMessage supporterVoice={voice} key={voice.id} />;
					})}
				</div>
			</div>
		</div>
	);
};
